import React, { useState, useMemo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import format from 'date-fns/format'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { DetailList } from '@tecuity/granite-ui/DetailList'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import Revert from '@tecuity/sos-icons/react/revert'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import SelectableCheckboxList, {
  CheckboxList,
} from '../SettingsViews/SettingsComponents/SelectableCheckboxList'
import { Spinner } from '../../Loaders/Loaders'
import { useRequestData } from '../../../Utilities/hooks'
import { getSourceTypeId } from '../../../Utilities/Utilities'
import { setErrorToast, setSuccessToast } from '../../../actions'
import * as api from '../../../requests'

const ContractorFilingTypeXref = () => {
  const dispatch = useDispatch()
  const imagePageTypes = useRequestData(api.GET_ALL_IMAGE_PAGE_TYPES, [], [])
  const recordAccessUserRoles = useRequestData(
    (cb, errCb) => {
      api.GET_RECORD_ACCESS_USER_ROLES_BY_SOURCE_TYPE(getSourceTypeId('contractor'), cb, errCb)
    },
    [],
    [],
  )
  const sourceTypes = useRequestData(api.GET_SOURCE_TYPE_OPTIONS, [], [])
  const formData = useRequestData(
    (cb, errCb) => api.GET_ALL_FORMS_BY_CATEGORY('Contractor', cb, errCb),
    [],
    [],
  )
  const appReports = useRequestData(api.GET_APP_REPORTS, [], [])
  const statusTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('Contractor', 'StatusType', cb, errCb),
    [],
    [],
  )
  const standingTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'StandingType', cb, errCb),
    [],
    [],
  )
  const durationTermTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'DurationTermType', cb, errCb),
    [],
    [],
  )
  const expirationReferenceTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'ExpirationReferenceType', cb, errCb),
    [],
    [],
  )
  const [recordTypeOptions, setRecordTypeOptions] = useState([])
  const [recordType, setRecordType] = useState(0)
  const [filingTypeList, setFilingTypeList] = useState([])
  const [activeFilingId, setActiveFilingId] = useState(0)
  const [loadingReceiptItemTypes, setLoadingReceiptItemTypes] = useState(false)
  const [receiptItemTypeIds, setReceiptItemTypeIds] = useState([])
  const [receiptItemTypes, setReceiptItemTypes] = useState([])
  const [originalIds, setOriginalIds] = useState([])
  const [checkedFilingTypeIds, setCheckedFilingTypeIds] = useState([])
  const [xRef, setXRef] = useState({ ...defaultDto })
  const [xRefMap] = useState({})
  const [loadingXref, setLoadingXref] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const disableXrefSettings = useMemo(() => {
    if (loadingXref) return true
    return !checkedFilingTypeIds.includes(activeFilingId)
  }, [activeFilingId, checkedFilingTypeIds, loadingXref])

  const changeXrefValue = (property, value) => {
    setXRef({ ...xRef, [property]: value })
    setIsDirty(true)
  }

  useEffect(() => {
    getLookupTypeOptions()
  }, [])

  useEffect(() => {
    xRefMap[xRef.ContractorFilingTypeId] = { ...xRef }
  }, [xRef])

  useEffect(() => {
    const controller = new AbortController()
    getContractorFilingTypeIdsByRecordType(controller)
    return () => {
      controller.abort()
    }
  }, [recordType])

  useEffect(() => {
    getXrefByRecordTypeFilingType(new AbortController())
  }, [activeFilingId, recordType])

  useEffect(() => {
    const promiseArray = []

    receiptItemTypeIds.forEach((id) => {
      promiseArray.push(
        new Promise((resolve, reject) => {
          api.GET_SETTINGS_RECEIPT_ITEM_TYPE_BY_ID(id, resolve, reject)
        }),
      )
    })

    if (promiseArray.length === 0) setLoadingReceiptItemTypes(false)

    Promise.all(promiseArray)
      .then((res) => {
        const array = []
        res.forEach((type) => {
          array.push(type)
        })
        setReceiptItemTypes(array)
        setLoadingReceiptItemTypes(false)
      })
      .catch((err) => {
        console.error(err)
      })
  }, [receiptItemTypeIds])

  useEffect(() => {
    const contractorSourceType = sourceTypes.find((type) => type.ModuleName === 'Contractor')
    const controller = new AbortController()
    if (contractorSourceType && activeFilingId && recordType) {
      setLoadingReceiptItemTypes(true)
      api.GET_SETTINGS_RECEIPT_ITEM_TYPE_XREF_BY_TYPES_SUB_TYPE(
        contractorSourceType.Id,
        recordType,
        activeFilingId,
        null,
        (res) => {
          setReceiptItemTypeIds(res.ReceiptItemTypeIds)
        },
        (err) => {
          console.error('retrieving', err)
          setReceiptItemTypeIds([])
        },
      )
    }
    return () => {
      controller.abort()
    }
  }, [activeFilingId, sourceTypes, recordType])

  const clickRevert = () => {
    Object.keys(xRefMap).forEach((key) => {
      delete xRefMap[key]
    })
    setIsDirty(false)
    getContractorFilingTypeIdsByRecordType(new AbortController())
    getXrefByRecordTypeFilingType(new AbortController())
    getLookupTypeOptions()
  }

  const clickSaveXref = () => {
    const promiseArray = []
    delete xRefMap['0']
    const changedFilingTypeIds = Object.keys(xRefMap)
    changedFilingTypeIds.forEach((filingTypeId) => {
      if (originalIds.includes(+filingTypeId)) {
        if (checkedFilingTypeIds.includes(+filingTypeId)) {
          //update the existing
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.SAVE_SETTINGS_CONTRACTOR_FILING_TYPE_XREF(
                'PUT',
                {
                  ...xRefMap[filingTypeId],
                },
                resolve,
                reject,
              )
            }),
          )
        } else {
          // delete the existing
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.DELETE_SETTINGS_CONTRACTOR_FILING_TYPE_XREF(
                xRefMap[filingTypeId].ContractorFilingTypeXrefId,
                resolve,
                reject,
              )
            }),
          )
        }
      } else if (checkedFilingTypeIds.includes(+filingTypeId)) {
        // add new xref
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.SAVE_SETTINGS_CONTRACTOR_FILING_TYPE_XREF(
              'POST',
              {
                ...xRefMap[filingTypeId],
              },
              resolve,
              reject,
            )
          }),
        )
      } else {
        // nothing changed
        console.warn('Contractor Xref data out of sync. This should NOT happen.')
      }
    })

    if (promiseArray.length > 0) {
      setLoadingXref(true)
      Promise.all(promiseArray)
        .then((xrefs) => {
          dispatch(setSuccessToast('Saved Setting(s) successfully.'))
          clickRevert()
          setLoadingXref(false)
        })
        .catch((err) => {
          if (err.DisplayToUser === true && (err.Message || '') !== '') {
            dispatch(setErrorToast(err.Message))
          } else {
            dispatch(setErrorToast('Failed to save setting.'))
          }
          setLoadingXref(false)
        })
    }
  }

  const getXrefByRecordTypeFilingType = (controller) => {
    if (activeFilingId && recordType) {
      setLoadingXref(true)
      api.GET_SETTINGS_CONTRACTOR_XREF_BY_RECORD_TYPE_FILING_TYPE(
        { signal: controller.signal },
        recordType,
        activeFilingId,
        (res) => {
          if (isDirty && Object.keys(xRefMap).includes(activeFilingId.toString())) {
            setXRef({ ...xRefMap[activeFilingId] })
          } else if (res) {
            setXRef({
              ...res,
            })
          } else {
            setXRef({
              ...defaultDto,
              ContractorRecordTypeId: recordType,
              ContractorFilingTypeId: activeFilingId,
            })
          }
          setLoadingXref(false)
        },
        (err) => {
          if (JSON.parse(err).status === 404) {
            setXRef({
              ...defaultDto,
              ContractorRecordTypeId: recordType,
              ContractorFilingTypeId: activeFilingId,
            })
          } else {
            console.error(err)
            if (JSON.parse(err).status !== 404) dispatch(setErrorToast('Failed to load Xref'))
          }
          setLoadingXref(false)
        },
      )
    }
  }

  const getContractorFilingTypeIdsByRecordType = (controller) => {
    if (recordType) {
      api.GET_SETTINGS_CONTRACTOR_FILING_TYPE_IDS_BY_RECORD_TYPE(
        { signal: controller.signal },
        recordType,
        (res) => {
          if (Array.isArray(res)) {
            setCheckedFilingTypeIds(res)
            setOriginalIds(res)
          }
        },
        (err) => {
          console.error(err)
          dispatch(setErrorToast('Failed to load selected Filing Type Ids'))
        },
      )
    }
  }

  const getLookupTypeOptions = () => {
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Contractor',
      'RecordType',
      (res) => {
        res.sort((a, b) => (a.Name > b.Name ? 1 : b.Name > a.Name ? -1 : 0))
        setRecordTypeOptions([
          ...res.map((t) => ({
            value: t.Id,
            label: t.Name,
          })),
        ])
        if (!recordType && res.length > 0) setRecordType(res[0].Id)
      },
      (err) => {
        setRecordTypeOptions([])
        console.error(err.message)
      },
    )
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Contractor',
      'FilingType',
      (res) => {
        res.sort((a, b) => (a.Name > b.Name ? 1 : b.Name > a.Name ? -1 : 0))
        setFilingTypeList(res)
      },
      (err) => {
        setFilingTypeList([])
        console.error(err.message)
      },
    )
  }

  return (
    <div className="settings-list-view">
      <h2>Contractor Filing Type Xref</h2>
      <div className="settings-content">
        <div className="settings-left-column">
          <div className="custom-correspondence-options">
            <SelectBox
              label="Record Type"
              noPlaceholder
              value={recordType || ''}
              onChange={(val) => {
                setRecordType(+val)
                setActiveFilingId(0)
              }}
              options={recordTypeOptions}
              disabled={isDirty ? true : undefined}
              description={isDirty ? 'Save or Revert changes to modify.' : undefined}
            />
          </div>
          <SelectableCheckboxList
            label={`Filing Types`}
            items={filingTypeList.map((type) => {
              return {
                id: type.Id,
                value: type.Id,
                label: type.Name,
              }
            })}
            activeId={activeFilingId}
            setActiveId={setActiveFilingId}
            checked={checkedFilingTypeIds}
            setChecked={(ids) => {
              setCheckedFilingTypeIds(ids)
              setIsDirty(true)
            }}
          />
        </div>
        <div className="properties-list scrollable">
          {/* DETAILS */}
          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Details
          </h3>

          <SelectBox
            label="Image Page Type"
            options={imagePageTypes
              .map((imagePageType) => {
                return {
                  label: imagePageType.Name,
                  value: imagePageType.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('ImagePageTypeId', +value)
            }}
            value={xRef.ImagePageTypeId || ''}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Form Title"
            options={formData
              .filter((form) => {
                return form.IsActive
              })
              .map((form) => {
                return {
                  label: form.Title,
                  value: form.FormDefinitionId,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('FormDefinitionId', +value)
            }}
            value={xRef.FormDefinitionId || ''}
            disabled={disableXrefSettings}
          />

          <div className="property-readonly">
            <label>{`Receipt Item(s) / Fee`}</label>
          </div>
          {loadingReceiptItemTypes && (
            <div className="inline-spinner">
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
          {!loadingReceiptItemTypes && (
            <DetailList
              topBorder
              bottomBorder
              details={
                receiptItemTypes.length > 0
                  ? receiptItemTypes.map((type) => {
                      const fee =
                        type.Fee === 'SQL Query'
                          ? type.Fee
                          : `$${Number(type.Fee)
                              .toFixed(2)
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                      return { label: type.Name, value: fee }
                    })
                  : [
                      {
                        label: 'No Receipt Items',
                        value: `Configure in 'Receipt Item to Filing Types' Fiscal Setting`,
                      },
                    ]
              }
            />
          )}

          {xRef.LastModifiedDate && (
            <div className="property-readonly">
              <label>
                {'Last Modified Date'}
                :&nbsp;
              </label>
              <div>{format(new Date(xRef.LastModifiedDate), 'M/dd/yyyy hh:mm aa')}</div>
            </div>
          )}

          {xRef.LastModifiedByName && (
            <div className="property-readonly">
              <label>
                {'Last Modified By'}
                :&nbsp;
              </label>
              <div>{xRef.LastModifiedByName}</div>
            </div>
          )}
          {xRef.ContractorFilingTypeXrefId && (
            <div className="property-readonly">
              <label>
                {'Xref Id'}
                :&nbsp;
              </label>
              <div>{xRef.ContractorFilingTypeXrefId}</div>
            </div>
          )}
          {/* REQUIREMENTS FOR FILING */}
          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Requirements to File
          </h3>

          <CheckboxList
            label="Record Access Roles Permitted to File (Leave blank to allow open filing)"
            items={recordAccessUserRoles
              .filter((x) => x.CanFileAmendments)
              .map((role) => ({
                id: role.RecordAccessUserRoleId,
                label: role.Name,
              }))
              .sort((a, b) => (a.label > b.label ? 1 : -1))}
            checkedIds={xRef.PermittedRecordAccessUserRoleIds || []}
            setChecked={(ids) => {
              changeXrefValue('PermittedRecordAccessUserRoleIds', ids)
            }}
          />

          <CheckboxList
            label={'Status to File'}
            items={statusTypes
              .map((type) => ({ id: type.Id, label: type.Name }))
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            checkedIds={xRef.PermittedContractorStatusTypeIds || []}
            setChecked={(ids) => {
              changeXrefValue('PermittedContractorStatusTypeIds', ids)
            }}
            disabled={disableXrefSettings}
          />

          <TextInput
            label={`Must File within Months of Formation`}
            value={xRef.MustFileWithinMonthsOfFormation || ''}
            onChange={(e) => {
              changeXrefValue(
                'MustFileWithinMonthsOfFormation',
                e.target.value === '' ? null : e.target.value,
              )
            }}
            disabled={disableXrefSettings}
            type="number"
          />

          <SelectBox
            label="Certificate of Licence (COL) Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorColStanding', +value)
            }}
            value={xRef.PriorColStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Renewal Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorRenewalStanding', +value)
            }}
            value={xRef.PriorRenewalStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Workforce Safety & Insurance (WSI) Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorWsiStanding', +value)
            }}
            value={xRef.PriorWsiStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Job Service Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorJobServiceStanding', +value)
            }}
            value={xRef.PriorJobServiceStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Business Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorBusinessStanding', +value)
            }}
            value={xRef.PriorBusinessStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <Checkbox
            label="Only Allow One Pending Filing Per User Per Record at a time"
            checked={xRef.OnlyAllowOnePendingFilingPerUser}
            onChange={(checked) => {
              changeXrefValue('OnlyAllowOnePendingFilingPerUser', checked)
            }}
            disabled={disableXrefSettings}
          />

          {/* AFTER APPROVAL */}
          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Results of Approved Filing
          </h3>

          <SelectBox
            label="Record Access Role Granted Post-Approval"
            options={recordAccessUserRoles
              .map((role) => {
                return {
                  label: role.Name,
                  value: role.RecordAccessUserRoleId,
                }
              })
              .sort((a, b) => {
                return a.value > b.value ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue(
                'NewRecordAccessUserRoleId',
                value === '' ? null : +value
              )
            }}
            value={xRef.NewRecordAccessUserRoleId || ''}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Contractor Status After Filing"
            options={[...statusTypes, { Name: 'No Change', Id: -1 }]
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label === 'No Change' ? -1 : a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewContractorStatusTypeId', +value)
            }}
            value={xRef.NewContractorStatusTypeId || -1}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Certificate of Licence (COL) Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewColStanding', +value)
            }}
            value={xRef.NewColStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Renewal Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewRenewalStanding', +value)
            }}
            value={xRef.NewRenewalStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Workforce Safety & Insurance (WSI) Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewWsiStanding', +value)
            }}
            value={xRef.NewWsiStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Job Service Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewJobServiceStanding', +value)
            }}
            value={xRef.NewJobServiceStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Business Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewBusinessStanding', +value)
            }}
            value={xRef.NewBusinessStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Duration Term"
            options={durationTermTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('DurationTermType', +value)
            }}
            value={xRef.DurationTermType || '0'}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Expiration Reference"
            options={expirationReferenceTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('ExpirationReference', +value)
            }}
            value={xRef.ExpirationReference || '0'}
            disabled={disableXrefSettings}
          />

          <Checkbox
            label="Include Original Image with Approval Stamp for Paper Filings"
            checked={xRef.IncludeImageForPaperFiling || false}
            onChange={(checked) => {
              changeXrefValue('IncludeImageForPaperFiling', checked)
            }}
            disabled={disableXrefSettings}
          />

          <CheckboxList
            label={'Generated Reports'}
            items={appReports
              .map((appReport) => ({ id: appReport.Id, label: appReport.Name }))
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            checkedIds={xRef.GeneratedReportIds || []}
            setChecked={(ids) => {
              changeXrefValue('GeneratedReportIds', ids)
            }}
            disabled={disableXrefSettings}
          />
          <div className="button-wrapper">
            {loadingXref && (
              <div className="loading-type">
                <Spinner size="small" themeColor="dark-neutral" />
              </div>
            )}
            <ContextButton
              variant="danger"
              disabled={!isDirty}
              leftIcon={
                <div className="checkmark-wrapper left">
                  <Revert />
                </div>
              }
              onClick={clickRevert}
            >
              Revert
            </ContextButton>
            <ContextButton
              variant="primary"
              disabled={!isDirty}
              rightIcon={
                <div className="checkmark-wrapper right">
                  <RightArrow />
                </div>
              }
              onClick={clickSaveXref}
            >
              Save
            </ContextButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContractorFilingTypeXref

const defaultDto = {
  ContractorFilingTypeXrefId: null,
  ContractorFilingTypeId: 0,
  ContractorRecordTypeId: 0,
  FormDefinitionId: null,
  ImagePageTypeId: null,
  PermittedRecordAccessUserRoleIds: [],
  PermittedContractorStatusTypeIds: [],
  MustFileWithinMonthsOfFormation: 0,
  PriorColStanding: 0,
  PriorRenewalStanding: 0,
  PriorWsiStanding: 0,
  PriorJobServiceStanding: 0,
  PriorBusinessStanding: 0,
  OnlyAllowOnePendingFilingPerUser: false,
  NewRecordAccessUserRoleId: 0,
  NewContractorStatusTypeId: 0,
  NewColStanding: 0,
  NewRenewalStanding: 0,
  NewWsiStanding: 0,
  NewJobServiceStanding: 0,
  NewBusinessStanding: 0,
  GeneratedReportIds: [],
  DurationTermType: 0,
  ExpirationReference: 0,
  IncludeImageForPaperFiling: false,
  LastModifiedDate: new Date(),
  LastModifiedByName: '',
}
